import "@/utils/extensions/libs/ArrayExtension";
import { AreaSharedCache } from "@/utils/AreaSharedCache";
import { IAreaItem } from "@/api/data/dto";

export interface IAreaOption extends IAreaItem {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  allowedChecked: boolean;
}
export interface ITag {
  id: number;
  name: string;
  fullName: string;
}
export class AreaExclusiveCache {
  deep: number;
  private readonly checkedMap: Map<number, Set<number>> = new Map();
  private readonly indeterminateSet: Set<number> = new Set<number>();
  constructor(deep: number) {
    this.deep = deep;
  }
  changeDeep(deep: number) {
    const changed = deep - this.deep;
    this.deep = deep;
    if (changed === 0) {
      return;
    } else if (changed === 1) {
      this.indeterminateSet.clear();
      const list = this.getCheckedLeaves();
      this.checkedMap.clear();
      list.forEach((it) => this.toggleChecked(it));
    } else if (changed === -1) {
      const list = Array.from(this.indeterminateSet);
      this.checkedMap.clear();
      this.indeterminateSet.clear();
      list.forEach((it) => this.toggleChecked(it));
    } else {
      this.checkedMap.clear();
      this.indeterminateSet.clear();
    }
  }
  async updateCheckedList(tree: Array<Array<number | Array<number>>>) {
    this.checkedMap.clear();
    this.indeterminateSet.clear();
    if (this.deep === 1) {
      await AreaSharedCache.loadChildren(0);
      this.checkedMap.set(0, new Set(tree.flat(2)));
      return;
    }
    const lastIndex = this.deep - 2;
    const parentList = tree
      .map((it) => {
        const array: number[] = [];
        for (let i = 0; i <= lastIndex; i++) {
          const item = it[i];
          if (Array.isArray(item)) {
            array.pushArray(item);
          } else {
            array.push(item);
          }
        }
        return array;
      })
      .flat();
    await AreaSharedCache.loadChildrenByList(parentList);
    tree.forEach((item) => {
      const id = item[lastIndex];
      const list = item[lastIndex + 1];
      if (!Array.isArray(id) && Array.isArray(list)) {
        this.checkedMap.set(id, new Set(list));
        this.adjustIndeterminate(id);
      } else {
        console.log("lastIndex:", lastIndex);
        console.log("id:", id);
        console.log("list:", list);
        console.log(Array.isArray(id), Array.isArray(list));
        console.error("格式错误:", item);
        console.error("deep:", this.deep);
        throw new Error("格式错误");
      }
    });
  }
  async setCheckedList(parentId: number, list: number[]) {
    if (this.deep === 1) {
      this.checkedMap.set(parentId, new Set<number>(list));
      return;
    }
    if (parentId && this.isLeaf(parentId)) {
      return;
    }
    const parent = AreaSharedCache.getItem(parentId);
    if (parent && parent.rank + 1 === this.deep) {
      this.checkedMap.set(parentId, new Set<number>(list));
      await this.adjustIndeterminate(parentId);
    } else {
      const result = list.map(async (it) => {
        await this.checkAllChildren(it);
        await this.adjustIndeterminate(it);
      });
      await Promise.all(result);
    }
  }
  getCheckedLeaves(): number[] {
    return Array.from(this.checkedMap.values()).flatMap((it) => Array.from(it));
  }
  getCheckedTree(): Array<Array<number | Array<number>>> {
    return Array.from(this.checkedMap.entries()).map(
      ([parentId, checkedList]) => {
        const grandIds = [];
        let parent = AreaSharedCache.getItem(parentId);
        while (parent) {
          grandIds.push(parent.id);
          parent = AreaSharedCache.getItem(parent.parentId);
        }
        const ids = grandIds.reverse();
        return [...ids, Array.from(checkedList)];
      }
    );
  }
  async getList(parentId: number): Promise<IAreaOption[]> {
    const children = await AreaSharedCache.loadChildren(parentId);
    if (!children || !children.length) {
      return [];
    }
    return children.map((it) => {
      if (this.isLeaf(it.id)) {
        const checkedList = this.checkedMap.get(parentId);
        const checked = checkedList?.has(it.id) ?? false;
        return {
          ...it,
          checked,
          allowedChecked: true,
          indeterminate: false,
          disabled: false,
        };
      }
      const allowedChecked = this.deep <= it.rank + 1;
      const checked = this.checkedMap.has(it.id);
      const indeterminate = this.indeterminateSet.has(it.id);
      return { ...it, checked, allowedChecked, indeterminate, disabled: false };
    });
  }
  getNames(showPrefix: boolean, separator: string): ITag[] {
    if (!this.checkedMap.size) {
      return [];
    }
    const entries = Array.from(this.checkedMap.entries());
    const s = separator === "/" || !showPrefix ? undefined : separator;
    return entries
      .map(([parentId, checkedList]) => {
        return this.getTags(parentId, Array.from(checkedList), s);
      })
      .flat();
  }
  cleanAll() {
    this.checkedMap.clear();
    this.indeterminateSet.clear();
  }
  private getTags(
    parentId: number,
    idList: number[],
    separator?: string
  ): ITag[] {
    if (!idList.length) {
      return [];
    }
    if (!separator) {
      return idList.map((it) => {
        const item = AreaSharedCache.getItem(it);
        const name = item?.name ?? `${it}`;
        const fullName = item?.fullName ?? `${it}`;
        return { id: it, name, fullName };
      });
    }
    const grandNames = [];
    let parent = AreaSharedCache.getItem(parentId);
    while (parent) {
      grandNames.push(parent.name);
      parent = AreaSharedCache.getItem(parent.parentId);
    }
    const names = grandNames.reverse();
    return idList.map((it) => {
      const name = AreaSharedCache.getItem(it)?.name ?? `${it}`;
      const fullName = [...names, name].join(separator);
      return { id: it, name, fullName };
    });
  }
  async toggleChecked(id: number) {
    const parentId = AreaExclusiveCache.getParentId(id);
    if (this.isLeaf(id)) {
      this.toggleCheckedItem(parentId, id);
      this.adjustIndeterminate(parentId);
      return;
    }
    if (this.indeterminateSet.has(id)) {
      //准备全选
      this.checkAllChildren(id);
    } else {
      const checkList = this.checkedMap.get(id);
      if (checkList && checkList.size) {
        // 准备取消全选
        this.checkedMap.delete(id);
      } else {
        this.checkAllChildren(id);
      }
    }
  }
  private async adjustIndeterminate(id: number) {
    if (id === 0) {
      return;
    }
    const checkedList = this.checkedMap.get(id);
    if (checkedList === undefined || !checkedList.size) {
      if (this.indeterminateSet.has(id)) {
        this.indeterminateSet.delete(id);
      }
      if (this.checkedMap.has(id)) {
        this.checkedMap.delete(id);
      }
      return;
    }
    const itemList = await AreaExclusiveCache.getChildrenIdSet(id, true);
    if (!itemList.size) {
      if (this.checkedMap.has(id)) {
        this.checkedMap.delete(id);
      }
      if (this.indeterminateSet.has(id)) {
        this.indeterminateSet.delete(id);
      }
      return;
    }
    const checkedItems = Array.from(itemList).filter((it) =>
      checkedList.has(it)
    );
    if (checkedItems.length === itemList.size) {
      // all children are checked
      if (this.indeterminateSet.has(id)) {
        this.indeterminateSet.delete(id);
      }
      return;
    }
    if (checkedList.size > checkedItems.length) {
      // 清除无效的id
      Array.from(checkedList)
        .filter((it) => !itemList.has(it))
        .forEach((it) => checkedList.delete(it));
    }
    if (!this.indeterminateSet.has(id)) {
      this.indeterminateSet.add(id);
    }
  }
  private async checkAllChildren(id: number) {
    if (this.isLeaf(id)) {
      return;
    }
    const itemList = await AreaExclusiveCache.getChildrenIdSet(id, true);
    this.checkedMap.set(id, itemList);
    if (this.indeterminateSet.has(id)) {
      this.indeterminateSet.delete(id);
    }
  }
  isChecked(parentId: number, id: number): boolean {
    if (this.checkedMap.has(parentId)) {
      const children = this.checkedMap.get(parentId);
      if (children) {
        return children.size > 0 && children.has(id);
      }
    }
    return false;
  }
  private toggleCheckedItem(parentId: number, childId: number) {
    this.checkedItem(parentId, childId, true);
  }
  removeCheckedItem(parentId: number, childId: number) {
    if (!this.checkedMap.has(parentId)) {
      return;
    }
    const children = this.checkedMap.get(parentId);
    if (children && children.has(childId)) {
      children.delete(childId);
    }
  }
  checkedItem(parentId: number, childId: number, toggle: boolean = false) {
    if (this.checkedMap.has(parentId)) {
      const children = this.checkedMap.get(parentId);
      if (children) {
        if (children.size && children.has(childId)) {
          if (toggle) {
            children.delete(childId);
          }
          return;
        }
        children.add(childId);
        return;
      }
    }
    this.checkedMap.set(parentId, new Set([childId]));
  }
  private static getParentId(id: number): number {
    return AreaSharedCache.getItem(id)?.parentId ?? 0;
  }
  private static async getChildrenIdSet(
    parentId: number,
    autoLoad: boolean = false
  ): Promise<Set<number>> {
    const children = await AreaExclusiveCache.getChildren(parentId, autoLoad);
    return new Set(children.map((it) => it.id));
  }
  private static async getChildren(
    parentId: number,
    autoLoad: boolean = false
  ): Promise<IAreaItem[]> {
    if (autoLoad) {
      return AreaSharedCache.loadChildren(parentId);
    }
    return AreaSharedCache.getChildren(parentId) || [];
  }
  isLeaf(id: number): boolean {
    if (AreaSharedCache.isLeaf(id)) {
      return true;
    }
    const item = AreaSharedCache.getItem(id);
    if (item === undefined) {
      throw new Error(`不存在的id:${id}`);
    }
    return item.rank >= this.deep;
  }
}
